import { useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import DepositDetailsModal from "../../components/deposit/DepositDetailsModal";

export default function DepositHistoryPage() {
  const { isDarkMode } = useTheme();
  const [filter, setFilter] = useState("all");
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const dark = isDarkMode;

  // Sample deposit history data
  const deposits = [
    {
      id: "TXN001",
      amount: 5000,
      method: "UPI",
      status: "completed",
      date: "2024-01-15 10:30 AM",
      transactionId: "UPI123456789",
      screenshot: "/screenshot1.png",
    },
    {
      id: "TXN002",
      amount: 2500,
      method: "Bank Transfer",
      status: "pending",
      date: "2024-01-15 09:15 AM",
      transactionId: "IMPS987654321",
      screenshot: "/screenshot2.png",
    },
    {
      id: "TXN003",
      amount: 10000,
      method: "PayTM",
      status: "completed",
      date: "2024-01-14 03:45 PM",
      transactionId: "PTM456789123",
      screenshot: "/screenshot3.png",
    },
    {
      id: "TXN004",
      amount: 1500,
      method: "Google Pay",
      status: "failed",
      date: "2024-01-14 11:20 AM",
      transactionId: "GPY789456123",
      screenshot: "/screenshot4.png",
    },
    {
      id: "TXN005",
      amount: 7500,
      method: "PhonePe",
      status: "processing",
      date: "2024-01-13 05:00 PM",
      transactionId: "PP321654987",
      screenshot: "/screenshot5.png",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#4CAF50";
      case "pending":
        return "#FF9800";
      case "processing":
        return "#2196F3";
      case "failed":
        return "#F44336";
      default:
        return "#666";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return "✓";
      case "pending":
        return "⏳";
      case "processing":
        return "⚡";
      case "failed":
        return "✗";
      default:
        return "•";
    }
  };

  const filteredDeposits = deposits.filter((deposit) => {
    if (filter === "all") return true;
    return deposit.status === filter;
  });

  const handleViewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setShowDetailsModal(true);
  };

  const totalDeposits = deposits.reduce((sum, d) => {
    return d.status === "completed" ? sum + d.amount : sum;
  }, 0);

  const pendingAmount = deposits.reduce((sum, d) => {
    return d.status === "pending" || d.status === "processing" ? sum + d.amount : sum;
  }, 0);

  const styles = {
    container: {
      padding: "2rem",
      maxWidth: 1200,
      margin: "0 auto",
    },
    header: {
      marginBottom: "2rem",
    },
    title: {
      fontSize: 28,
      fontWeight: 700,
      background: dark
        ? "linear-gradient(135deg, #d4af37 0%, #f0c842 100%)"
        : "linear-gradient(135deg, #b8882a 0%, #d4a843 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      marginBottom: "0.5rem",
    },
    subtitle: {
      fontSize: 14,
      color: dark ? "#888" : "#666",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    statCard: {
      background: dark
        ? "linear-gradient(135deg, #1a1814 0%, #2d2823 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: 16,
      padding: "1.5rem",
      border: dark ? "1px solid rgba(212,175,55,.14)" : "1px solid rgba(180,155,100,.22)",
      boxShadow: dark
        ? "0 4px 20px rgba(0,0,0,0.3)"
        : "0 4px 20px rgba(0,0,0,0.08)",
    },
    statIcon: {
      width: 50,
      height: 50,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 24,
      marginBottom: "1rem",
    },
    statLabel: {
      fontSize: 12,
      color: dark ? "#888" : "#666",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: "0.5rem",
    },
    statValue: {
      fontSize: 24,
      fontWeight: 700,
      color: dark ? "#f0ece2" : "#2c2416",
    },
    filterSection: {
      background: dark
        ? "linear-gradient(135deg, #1a1814 0%, #2d2823 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: 16,
      padding: "1.5rem",
      marginBottom: "2rem",
      border: dark ? "1px solid rgba(212,175,55,.14)" : "1px solid rgba(180,155,100,.22)",
    },
    filterTabs: {
      display: "flex",
      gap: "1rem",
      flexWrap: "wrap",
    },
    filterTab: {
      padding: "0.5rem 1rem",
      background: dark ? "#2d2823" : "#f0f0f0",
      border: "none",
      borderRadius: 25,
      color: dark ? "#888" : "#666",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.3s",
      textTransform: "capitalize",
    },
    activeFilter: {
      background: "linear-gradient(135deg, #d4af37 0%, #f0c842 100%)",
      color: "#fff",
    },
    historyList: {
      display: "grid",
      gap: "1rem",
    },
    historyItem: {
      background: dark
        ? "linear-gradient(135deg, #1a1814 0%, #2d2823 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: 16,
      padding: "1.5rem",
      border: dark ? "1px solid rgba(212,175,55,.14)" : "1px solid rgba(180,155,100,.22)",
      boxShadow: dark
        ? "0 4px 20px rgba(0,0,0,0.3)"
        : "0 4px 20px rgba(0,0,0,0.08)",
      transition: "all 0.3s",
      cursor: "pointer",
    },
    itemHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "1rem",
      flexWrap: "wrap",
      gap: "1rem",
    },
    itemAmount: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#d4af37" : "#b8882a",
    },
    itemStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
      padding: "6px 12px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    itemDetails: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "1rem",
    },
    itemRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: 13,
      color: dark ? "#aaa" : "#666",
    },
    viewButton: {
      padding: "8px 16px",
      background: dark
        ? "rgba(212, 175, 55, 0.2)"
        : "rgba(184, 136, 42, 0.1)",
      border: "none",
      borderRadius: 10,
      color: dark ? "#d4af37" : "#b8882a",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.3s",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    emptyState: {
      textAlign: "center",
      padding: "4rem 2rem",
      background: dark
        ? "linear-gradient(135deg, #1a1814 0%, #2d2823 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      borderRadius: 16,
      border: dark ? "1px solid rgba(212,175,55,.14)" : "1px solid rgba(180,155,100,.22)",
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: "1rem",
      opacity: 0.5,
    },
    emptyText: {
      fontSize: 16,
      color: dark ? "#888" : "#666",
      lineHeight: 1.6,
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Deposit History</h1>
        <p style={styles.subtitle}>Track all your deposit transactions and their status</p>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIcon,
              background: "rgba(76, 175, 80, 0.2)",
            }}
          >
            💰
          </div>
          <div style={styles.statLabel}>Total Deposits</div>
          <div style={styles.statValue}>₹{totalDeposits.toLocaleString()}</div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIcon,
              background: "rgba(255, 152, 0, 0.2)",
            }}
          >
            ⏳
          </div>
          <div style={styles.statLabel}>Pending Amount</div>
          <div style={styles.statValue}>₹{pendingAmount.toLocaleString()}</div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIcon,
              background: "rgba(33, 150, 243, 0.2)",
            }}
          >
            📊
          </div>
          <div style={styles.statLabel}>Total Transactions</div>
          <div style={styles.statValue}>{deposits.length}</div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIcon,
              background: "rgba(212, 175, 55, 0.2)",
            }}
          >
            ✓
          </div>
          <div style={styles.statLabel}>Success Rate</div>
          <div style={styles.statValue}>
            {Math.round((deposits.filter(d => d.status === 'completed').length / deposits.length) * 100)}%
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div style={styles.filterSection}>
        <div style={styles.filterTabs}>
          {["all", "completed", "pending", "processing", "failed"].map((status) => (
            <button
              key={status}
              style={{
                ...styles.filterTab,
                ...(filter === status ? styles.activeFilter : {}),
              }}
              onClick={() => setFilter(status)}
              onMouseEnter={(e) => {
                if (filter !== status) {
                  e.target.style.background = dark
                    ? "rgba(212, 175, 55, 0.2)"
                    : "rgba(184, 136, 42, 0.1)";
                  e.target.style.color = dark ? "#d4af37" : "#b8882a";
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== status) {
                  e.target.style.background = dark ? "#2d2823" : "#f0f0f0";
                  e.target.style.color = dark ? "#888" : "#666";
                }
              }}
            >
              {status} ({deposits.filter(d => status === 'all' ? true : d.status === status).length})
            </button>
          ))}
        </div>
      </div>

      {/* Deposits List */}
      <div style={styles.historyList}>
        {filteredDeposits.length > 0 ? (
          filteredDeposits.map((deposit) => (
            <div
              key={deposit.id}
              style={styles.historyItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = dark
                  ? "0 8px 30px rgba(0,0,0,0.4)"
                  : "0 8px 30px rgba(0,0,0,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = dark
                  ? "0 4px 20px rgba(0,0,0,0.3)"
                  : "0 4px 20px rgba(0,0,0,0.08)";
              }}
            >
              <div style={styles.itemHeader}>
                <div>
                  <div style={styles.itemAmount}>₹{deposit.amount.toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: dark ? "#888" : "#666", marginTop: "0.25rem" }}>
                    Transaction ID: {deposit.id}
                  </div>
                </div>
                <div
                  style={{
                    ...styles.itemStatus,
                    background: `${getStatusColor(deposit.status)}20`,
                    color: getStatusColor(deposit.status),
                  }}
                >
                  <span>{getStatusIcon(deposit.status)}</span>
                  <span>{deposit.status}</span>
                </div>
              </div>

              <div style={styles.itemDetails}>
                <div style={styles.itemRow}>
                  <span>Method:</span>
                  <span style={{ fontWeight: 600 }}>{deposit.method}</span>
                </div>
                <div style={styles.itemRow}>
                  <span>Date:</span>
                  <span style={{ fontWeight: 600 }}>{deposit.date}</span>
                </div>
                <div style={styles.itemRow}>
                  <span>Reference:</span>
                  <span style={{ fontWeight: 600, fontSize: 11 }}>{deposit.transactionId}</span>
                </div>
              </div>

              <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
                <button
                  style={styles.viewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(deposit);
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = dark
                      ? "rgba(212, 175, 55, 0.3)"
                      : "rgba(184, 136, 42, 0.2)";
                    e.target.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = dark
                      ? "rgba(212, 175, 55, 0.2)"
                      : "rgba(184, 136, 42, 0.1)";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  <span>👁️</span>
                  View Details
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <div style={styles.emptyText}>
              No deposits found for the selected filter.
              <br />
              Try selecting a different status or make your first deposit.
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <DepositDetailsModal
          deposit={selectedDeposit}
          onClose={() => setShowDetailsModal(false)}
          isDarkMode={dark}
          isMobile={window.innerWidth < 768}
        />
      )}
    </div>
  );
}